import { z } from 'zod';
import { NoticeCodeSchema } from '../assistant/notice-codes';
import { DocumentCategorySchema } from '../property/documents.schemas';

/**
 * What the member is shown before an `approval_required` tool call is allowed
 * to run over WhatsApp (IMM-1040.3): a server-rendered preview of the exact,
 * frozen arguments, never the model's own prose. Two kinds today:
 * `DOCUMENT_REQUEST` (`property.createDocumentRequest`) and
 * `VIEWING_INVITATION` (`communications.inviteProspectToViewing`, IMM-1067.5).
 */
const DocumentRequestProposalSchema = z.object({
  kind: z.literal('DOCUMENT_REQUEST'),
  propertyLabel: z.string(),
  categories: z.array(DocumentCategorySchema).min(1),
  note: z.string().min(1).nullable(),
});

const ViewingInvitationProposalSchema = z.object({
  kind: z.literal('VIEWING_INVITATION'),
  // For the model-context draft line; never rendered to the member.
  prospectId: z.uuid(),
  // „{Kontakt} · {Objekt}", shown to the member only.
  prospectLabel: z.string(),
  startsAt: z.iso.datetime(),
  endsAt: z.iso.datetime(),
  timeZone: z.string(),
  hint: z.string().min(1).nullable(),
});

export const WhatsAppSendProposalSchema = z.discriminatedUnion('kind', [
  DocumentRequestProposalSchema,
  ViewingInvitationProposalSchema,
]);
export type WhatsAppSendProposal = z.infer<typeof WhatsAppSendProposalSchema>;

/**
 * The assistant's answer to a WhatsApp turn (IMM-1040.2, extended IMM-1040.3).
 * Exactly one of `text` / `noticeCode` / `proposal` is set: a normal end turn
 * carries the reply text, a stopped/failed run carries the notice code, and a
 * proposed send (an `approval_required` tool call awaiting the member's „Ja")
 * carries the rendered preview — mirroring the chat path's NOTICE message,
 * which this event has no equivalent transcript row for on this side of the
 * module boundary.
 */
export const WhatsAppReplyProducedPayloadSchema = z
  .object({
    assistantReplyId: z.uuid(),
    runId: z.uuid(),
    linkId: z.uuid(),
    workspaceId: z.uuid(),
    memberId: z.uuid(),
    text: z.string().min(1).nullable(),
    noticeCode: NoticeCodeSchema.nullable(),
    proposal: WhatsAppSendProposalSchema.nullable(),
  })
  .refine(
    (payload) => [payload.text, payload.noticeCode, payload.proposal].filter((value) => value !== null).length === 1,
    { message: 'Exactly one of text/noticeCode/proposal must be set.' },
  );
export type WhatsAppReplyProducedPayload = z.infer<typeof WhatsAppReplyProducedPayloadSchema>;
